import React from "react";
import get from "lodash/get";
import styled from "styled-components";

import AppOpticsLogo from "components/svg/appoptics-logo.svg";
import {
  BILLING_COUNTRIES,
  CANADA_PROVINCES,
} from "organization/Billing/constants";
import { InputLabel, Select, TextInput } from "components/Form";
import { PrimaryButton } from "components/Button";
import EditionLogo from "organization/components/EditionLogo";
import { AlignmentSimple } from "components/Alignment";
import { SUBSCRIPTION_MODE } from "organization/Billing/constants";
import { DeveloperPackDetails } from "organization/Upgrade/DeveloperPackDetails";

const ALERT = "alert";
const NOTICE = "notice";
const DEVELOPER = "developer";
export const DEFAULT_PROVINCE = "AB";

const errorFormatter = ({ errors = {}, flash = [] }) => {
  // Convert flash and resource errors to the same format
  const flashErrors = flash
    .filter(([field, _]) => field === ALERT)
    .map(([field, error]) => error);

  const resourceErrors = Object.keys(errors)
    .map(field => errors[field].map(error => `${field} ${error}`))
    .reduce((a, b) => a.concat(b), []);

  return flashErrors.concat(resourceErrors)[0];
};

const noticeFormatter = (flash = []) => {
  return flash
    .filter(([field, _]) => field === NOTICE)
    .map(([field, message]) => message)[0];
};

const Info = styled.div`
  background: ${p => (p.type === "error" ? p.theme.error : p.theme.success)};
  padding: 10px;
  color: white;
  margin-bottom: 20px;
`;

const Flash = ({ type, message }) => {
  return message ? (
    <Info type={type} dangerouslySetInnerHTML={{ __html: message }} />
  ) : null;
};

export const CountrySelect = props => {
  return (
    <InputLabel>
      Country *
      <Select
        name="user[country]"
        onChange={props.onChangeFor}
        value={props.country || "US"}
        options={BILLING_COUNTRIES}
      />
    </InputLabel>
  );
};

export const CanadaProvince = props => {
  return (
    <InputLabel>
      Province *
      <Select
        name="user[province]"
        onChange={props.onChangeFor}
        value={props.province || DEFAULT_PROVINCE}
        options={CANADA_PROVINCES}
      />
    </InputLabel>
  );
};

const FormWrapper = styled.div`
  background: ${p => p.theme.Auth.background};
  border: ${p => p.theme.border};
  max-height: 100%;
  overflow-x: visible;
  overflow-y: auto;
  padding: 30px;
  width: 500px;
  text-align: center;

  a {
    color: ${p => p.theme.link};
    font-weight: 600;
  }

  ${Info} {
    a {
      color: white;
      text-decoration: underline;
    }
  }
`;

const Text = styled.p`
  font-size: 13px;
  color: ${p => p.theme.text};
  margin: -10px 0 20px;
`;

const Title = styled.h1`
  font-weight: normal;
  font-size: 21px;
  color: ${p => p.theme.text};
  margin: 20px 0;
`;

const EditionHeader = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${p => p.theme.ContractDetails.Heading.color};
  margin-top: 30px;
`;

const authToken = get(window, "Auth.authenticity_token", null);
const errors = get(window, "Auth.resource_errors", {});
const flash = get(window, "Auth.flash", []);

export class Form extends React.Component {
  state = {
    email:
      get(window, "Auth.user_params.email", null) || // retained email from failed login
      get(window, "Auth.collaborator_email", null) ||
      get(window, "Auth.demo_params.username", null),
    password: get(window, "Auth.demo_params.password", null),
  };

  onEmailChange = e => {
    this.setState({ email: e.target.value });
  };

  onPasswordChange = e => {
    this.setState({ password: e.target.value });
  };

  render() {
    const {
      title,
      action,
      text,
      show_email,
      show_collaborator_email,
      show_password,
      invalid,
      onSubmit,
      edition,
    } = this.props;
    const { email, password } = this.state;
    return (
      <FormWrapper>
        <AppOpticsLogo />

        <form
          action={action}
          method="post"
          onSubmit={invalid ? e => e.preventDefault() : onSubmit || null}
        >
          {title && <Title>{title}</Title>}
          {text && <Text>{text}</Text>}
          {edition && (
            <React.Fragment>
              <EditionHeader>{"Get Started With AppOptics"}</EditionHeader>
              <AlignmentSimple horizontal="center">
                <EditionLogo mode={SUBSCRIPTION_MODE[edition.toUpperCase()]} />
              </AlignmentSimple>
              {edition === DEVELOPER && <DeveloperPackDetails />}
            </React.Fragment>
          )}

          <input type="hidden" name="authenticity_token" value={authToken} />

          <Flash type="error" message={errorFormatter({ errors, flash })} />
          <Flash type="notice" message={noticeFormatter(flash)} />

          {show_email && (
            <Email
              autoFocus={!email || !show_password}
              name="user[email]"
              placeholder="Your email"
              onChange={this.onEmailChange}
              value={email || ""}
            />
          )}
          {show_collaborator_email && (
            <Email
              name="user[email]"
              value={email || ""}
              onChange={this.onEmailChange}
              readOnly
            />
          )}
          {show_password && (
            <Password
              autoFocus={!!email || show_collaborator_email}
              name="user[password]"
              placeholder="Your password"
              value={password || ""}
              onChange={this.onPasswordChange}
            />
          )}
          {this.props.children}
        </form>
      </FormWrapper>
    );
  }
}

export const Footer = styled.div`
  padding-top: 20px;
`;

export const Divider = styled.span`
  display: inline-block;
  height: 11px;
  width: 0;
  border-left: ${p => p.theme.border};
  margin: 0 10px;
`;

export const Paragraph = styled.p`
  color: ${p => p.theme.textLight};
  font-size: 10px;
  line-height: 17px;
  width: 100%;
  margin: 0 auto;
`;

export const Button = styled(PrimaryButton)`
  width: 60%;
  margin-top: 10px;
  font-weight: 600;
  font-size: 13px;
`;
export const Input = styled(TextInput)`
  margin-bottom: ${p => p.theme.padding};
`;
export const Email = styled(Input).attrs({ type: "email" })``;
export const Password = styled(Input).attrs({ type: "password" })``;
